import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { aboutPageContent } from '@/mocks/about';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative h-[420px] md:h-[540px] overflow-hidden">
          <img
            src={aboutPageContent.heroImage}
            alt="关于析莜"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
          <div className="relative h-full flex flex-col justify-center px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <nav className="mb-6 flex items-center gap-2 text-sm text-white/70">
              <Link to="/" className="hover:text-white transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-white font-medium">关于我们</span>
            </nav>
            <h1 className="text-3xl md:text-5xl font-black text-white font-heading tracking-wide">
              {aboutPageContent.titleCn}
            </h1>
            <p className="mt-3 text-base md:text-lg text-white/70 font-label">
              {aboutPageContent.titleEn}
            </p>
          </div>
        </section>

        {/* Intro */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-50 font-heading">
                  {aboutPageContent.titleCn}
                </h2>
                <div className="mt-4 w-12 h-0.5 bg-primary-500 rounded-full" />
                <p className="mt-6 text-sm md:text-base text-foreground-300 leading-loose">
                  {aboutPageContent.description}
                </p>
                {/* Cities */}
                <div className="mt-8">
                  <h3 className="text-sm font-semibold text-foreground-200 mb-3 font-label">业务覆盖城市</h3>
                  <div className="flex flex-wrap gap-3">
                    {aboutPageContent.cities.map((city) => (
                      <span
                        key={city}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 border border-background-200/50 text-sm text-foreground-200"
                      >
                        <i className="ri-map-pin-line text-primary-400" />
                        {city}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-1 gap-4">
                {aboutPageContent.stats.map((stat) => (
                  <div
                    key={stat.subLabel}
                    className="flex items-center gap-6 p-6 rounded-lg bg-background-100 border border-background-200/50"
                  >
                    <p className="text-3xl md:text-4xl font-black text-primary-400 font-heading shrink-0">
                      {stat.label}
                    </p>
                    <p className="text-sm md:text-base text-foreground-200 font-medium">
                      {stat.subLabel}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Core Features */}
        <section className="py-16 md:py-24 bg-background-100">
          <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-50 font-heading">
                核心能力
              </h2>
              <div className="mt-4 mx-auto w-12 h-0.5 bg-primary-500 rounded-full" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {aboutPageContent.features.map((feature) => (
                <div
                  key={feature.title}
                  className="group p-6 md:p-8 rounded-lg bg-background-50 border border-background-200/50 hover:border-primary-500/40 transition-all duration-300"
                >
                  <div className="w-14 h-14 flex items-center justify-center rounded-lg bg-background-100 text-primary-400 mb-5 group-hover:bg-primary-500 group-hover:text-foreground-50 transition-colors">
                    <i className={`${feature.icon} text-2xl`} />
                  </div>
                  <h3 className="text-lg font-bold text-foreground-50">
                    {feature.title}
                  </h3>
                  <p className="mt-3 text-sm text-foreground-300 leading-relaxed">
                    {feature.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Certificates */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-50 font-heading">
                资质与荣誉
              </h2>
              <div className="mt-4 mx-auto w-12 h-0.5 bg-primary-500 rounded-full" />
              <p className="mt-4 text-sm text-foreground-300 max-w-2xl mx-auto">
                析莜科技已通过多项行业权威认证，拥有核心技术自主知识产权，持续为客户提供安全可靠的解决方案
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {aboutPageContent.certificates.map((cert, index) => (
                <div
                  key={cert.title}
                  className="group relative p-6 md:p-8 rounded-lg bg-background-100 border border-background-200/50 hover:border-primary-500/40 transition-all duration-300 overflow-hidden"
                >
                  <span className="absolute top-4 right-4 text-5xl font-black text-background-200 group-hover:text-primary-400/20 transition-colors font-heading leading-none">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div className="relative">
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-background-50 text-primary-400 mb-5 group-hover:bg-primary-500 group-hover:text-foreground-50 transition-colors">
                      <i className="ri-award-line text-xl" />
                    </div>
                    <h3 className="text-base md:text-lg font-bold text-foreground-50">
                      {cert.title}
                    </h3>
                    <p className="mt-3 text-sm text-foreground-300 leading-relaxed">
                      {cert.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}